// Trip history (Figma H2 / H3) labels and grouping. Solid-free so bun tests can import it.
import type { PaymentMethodView, TripHistoryItem } from "../shared/api";
import { billedLine } from "../shared/rate";
import { euros, minutes } from "./format";

const TZ = "Europe/Paris";

const fmt = (opts: Intl.DateTimeFormatOptions, tz: string) =>
  new Intl.DateTimeFormat("fr-FR", { timeZone: tz, ...opts });

/** Calendar parts of an instant in `tz` ("2026", "10", "14"). */
function parts(ms: number, tz: string) {
  const p = fmt({ year: "numeric", month: "2-digit", day: "2-digit" }, tz).formatToParts(ms);
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return { y: get("year"), m: get("month"), d: get("day") };
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export interface DayGroup {
  key: string;
  /** "Aujourd'hui" or "30 septembre". */
  label: string;
  items: TripHistoryItem[];
}
export interface MonthGroup {
  key: string;
  /** "Octobre 2026". */
  label: string;
  /** Sum of the known prices of the loaded rides of that month. */
  totalCents: number;
  days: DayGroup[];
}

/** Figma H2: a summary card per month, then rides under day headers, in the given order. */
export function groupHistory(items: TripHistoryItem[], now: number, tz = TZ): MonthGroup[] {
  const today = parts(now, tz);
  const todayKey = `${today.y}-${today.m}-${today.d}`;
  const months: MonthGroup[] = [];
  for (const item of items) {
    const at = Date.parse(item.startedAt);
    const { y, m, d } = parts(at, tz);
    const monthKey = `${y}-${m}`;
    let month = months.at(-1);
    if (month?.key !== monthKey) {
      month = {
        key: monthKey,
        label: capitalize(fmt({ month: "long", year: "numeric" }, tz).format(at)),
        totalCents: 0,
        days: [],
      };
      months.push(month);
    }
    month.totalCents += item.priceCents ?? 0;
    const dayKey = `${monthKey}-${d}`;
    let day = month.days.at(-1);
    if (day?.key !== dayKey) {
      day = {
        key: dayKey,
        label:
          dayKey === todayKey
            ? "Aujourd'hui"
            : fmt({ day: "numeric", month: "long" }, tz).format(at),
        items: [],
      };
      month.days.push(day);
    }
    day.items.push(item);
  }
  return months;
}

/** "17:47" in Paris time. */
export const clockLabel = (iso: string, tz = TZ) =>
  fmt({ hour: "2-digit", minute: "2-digit", hour12: false }, tz).format(Date.parse(iso));

/** "2 octobre 2026". */
export const receiptDate = (iso: string, tz = TZ) =>
  fmt({ day: "numeric", month: "long", year: "numeric" }, tz).format(Date.parse(iso));

export const rowTitle = (i: TripHistoryItem) => `Trajet vélo · ${i.vehicleId}`;

/** "17:47 · 8 min · Apple Pay" (method left out when unknown). */
export const rowSubtitle = (i: TripHistoryItem, method: string | undefined) =>
  [clockLabel(i.startedAt), minutes(i.durationSec), method].filter(Boolean).join(" · ");

/** How the ride was paid: our payment method for rides made here, the Pony app otherwise. */
export function paidWithLabel(
  i: TripHistoryItem,
  m: PaymentMethodView | null | undefined,
): string | undefined {
  if (!i.inApp) return "App Pony";
  if (!m) return undefined;
  if (m.wallet === "apple_pay") return "Apple Pay";
  return m.last4 ? `Carte •••• ${m.last4}` : "Carte";
}

export const amountLabel = (i: TripHistoryItem) =>
  i.priceCents === undefined ? "—" : euros(i.priceCents);

/** Figma H3 price block: unlock, "8 min × 0,21 €", total. Only the total without our plan. */
export function priceLines(i: TripHistoryItem): {
  unlock?: string;
  billed?: string;
  billedAmount?: string;
  total: string;
} {
  const total = amountLabel(i);
  if (!i.inApp || i.priceCents === undefined || i.unlockCents === undefined || !i.priceLabel)
    return { total };
  return {
    unlock: i.unlockCents === 0 ? "Offert" : euros(i.unlockCents),
    billed: billedLine(i.billedMinutes, i.priceLabel),
    billedAmount: euros(Math.max(0, i.priceCents - i.unlockCents)),
    total,
  };
}

/** Rides seen in the list, by id: the receipt shows them at once while it refreshes from the BFF. */
export const seenRides = new Map<string, TripHistoryItem>();
